/**
 * FeedbackThread
 * Chat-bubble style rendering of a feedback thread + an inline reply box.
 * Used by both the user-facing FeedbackDialog and admin AdminFeedback panels.
 */
import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Send, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-GB', {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return '';
  }
};

const FeedbackThread = ({ feedbackId, thread = [], viewerRole, onMessageSent, compact = false, endpoint }) => {
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [thread]);

  const handleSend = async () => {
    const body = reply.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      const url = endpoint || `${API_URL}/api/feedback/${feedbackId}/message`;
      await axios.post(url, { message: body });
      setReply('');
      if (onMessageSent) onMessageSent();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-2" data-testid={`feedback-thread-${feedbackId}`}>
      {/* Thread bubbles */}
      <div className={`space-y-2 ${compact ? 'max-h-56' : 'max-h-72'} overflow-y-auto pr-1`}>
        {thread.length === 0 && (
          <p className="text-xs text-slate-500 text-center py-4">No messages yet.</p>
        )}
        {thread.map((msg) => {
          const isMine = msg.sender_role === viewerRole;
          const bubbleCls = isMine
            ? 'bg-amber-500/15 border-amber-500/30 text-amber-100'
            : msg.sender_role === 'admin'
              ? 'bg-sky-500/15 border-sky-500/30 text-sky-100'
              : 'bg-slate-700/40 border-slate-600/50 text-slate-200';
          return (
            <div
              key={msg.id}
              className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}
              data-testid={`thread-msg-${msg.id}`}
            >
              <div className={`max-w-[85%] border rounded-xl px-3 py-2 ${bubbleCls}`}>
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span className="text-[10px] font-semibold uppercase opacity-80">
                    {msg.sender_role === 'admin' ? 'Admin' : (msg.sender_name || 'You')}
                  </span>
                  <span className="text-[10px] opacity-50">{formatTime(msg.created_at)}</span>
                </div>
                <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.message}</p>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Reply box */}
      <div className="flex gap-2 pt-2 border-t border-slate-700/50">
        <input
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          placeholder="Type your message..."
          className="flex-1 h-8 px-3 rounded-md text-sm bg-slate-900 border border-slate-600 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500/50"
          data-testid={`thread-reply-input-${feedbackId}`}
          disabled={sending}
        />
        <Button
          size="sm"
          onClick={handleSend}
          disabled={sending || !reply.trim()}
          className="bg-amber-600 hover:bg-amber-700 h-8 text-xs shrink-0"
          data-testid={`thread-reply-send-${feedbackId}`}
        >
          {sending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
        </Button>
      </div>
    </div>
  );
};

export default FeedbackThread;
